import * as React from 'react';
import { useEffect, useState } from 'react';

/** Material UI Packages. */
import { Box, Paper, Tab, Tabs } from '@mui/material';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

import CircularLoading from 'shared/ui/CircularLoading';

/** Redux Slices. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { setClientList } from './store/clientListSlice';
import { setItemCategories } from './store/itemCategoriesSlice';

/** Internal Components */
import { callAPI } from 'shared/api/callApi';
import { customized_style } from 'shared/config/Styles';
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import TabPanel from 'shared/ui/TabPanel';

import DutyEntries from './components/DutyEntries/DutyEntries';
import JinroERP from './components/JinroERP/JinroERP';
import DutyConfiguration from './components/DutyConfiguration/DutyConfiguration';

/** Tab labels for Duty Calculator views. */
const TAB_LABELS = ['DUTY ENTRIES', 'JINRO ERP CHART', 'DUTY CONFIGURATION'];

export default function DutyCalculator() {
    const dispatch = useDispatch();
    /** Provides Auth0 helpers and the authenticated user profile. */
    const { user, getAccessTokenSilently } = useAuth0();
    /** Stores the signed-in user's email for API requests. */
    const userEmail = user?.email ?? null;

    const [loadingState, setLoadingState] = useState({
        clientList: false,
        itemCategories: false,
        dutyEntries: false,
        dutyEntryDetail: false,
        jinroErpChart: false,
        itemList: false,
        itemFormulas: false,
        weekList: false,
        jinroSummaries: false,
        circularLoading: false,
    });

    /** Current selected tab. */
    const [tabValue, setTabValue] = useState(0);
    const [itemFormulasRows, setItemFormulasRows] = useState([]);

    /** Document title on component mount. */
    useEffect(() => {
        document.title = 'Duty Calculator | Operation System';
        callAPI({
            method: 'get',
            module: 'trade_partners',
            component: 'Client',
            getAccessTokenSilently,
            email: userEmail,
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, tradePartners: true }));
            },
            onSuccess: (resp) => {
                console.log('trade partners response', resp);
                dispatch(setClientList(resp?.data ?? []));
            },
            onError: () => {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Failed to load trade partners. Please try again.',
                        severity: 'error',
                    }),
                );
            },
            onFinal: () => {
                setLoadingState((prev) => ({ ...prev, tradePartners: false }));
            },
        });
        callAPI({
            method: 'get',
            module: 'duty_calculator',
            component: 'duty_item_categories',
            getAccessTokenSilently,
            email: userEmail,
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, itemCategories: true }));
            },
            onSuccess: (resp) => {
                console.log('item categories response', resp);
                dispatch(setItemCategories(resp?.data ?? []));
            },
            onError: () => {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Failed to load item categories. Please try again.',
                        severity: 'error',
                    }),
                );
            },
            onFinal: () => {
                setLoadingState((prev) => ({ ...prev, itemCategories: false }));
            },
        });

        callAPI({
            method: 'get',
            module: 'duty_calculator',
            component: 'duty_formulas',
            getAccessTokenSilently,
            email: userEmail,
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, itemFormulas: true }));
            },
            onSuccess: (resp) => {
                console.log('Item formulas response:', resp);
                setItemFormulasRows(resp?.data ?? []);
            },
            onError: () => {
                console.error('Failed to load item formulas');
            },
            onFinal: () => {
                setLoadingState((prev) => ({ ...prev, itemFormulas: false }));
            },
        });
    }, []);

    /** Handler for tab change event. */
    const handleTabChange = (_event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <>
            <Paper sx={{ ...customized_style.itemPaper, p: 0 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    aria-label="duty calculator tabs"
                    sx={{
                        borderBottom: 1,
                        borderColor: '#d2d2d4',
                    }}
                >
                    {TAB_LABELS.map((label, index) => (
                        <Tab
                            key={label}
                            label={label}
                            id={`duty-tab-${index}`}
                            aria-controls={`duty-tabpanel-${index}`}
                        />
                    ))}
                </Tabs>
                <TabPanel value={tabValue} index={0} id="duty-tabpanel-0" aria-labelledby="duty-tab-0">
                    <DutyEntries
                        userEmail={userEmail}
                        getAccessTokenSilently={getAccessTokenSilently}
                        loadingState={loadingState}
                        setLoadingState={setLoadingState}
                        itemFormulasRows={itemFormulasRows}
                    />
                </TabPanel>
                <TabPanel value={tabValue} index={1} id="duty-tabpanel-1" aria-labelledby="duty-tab-1">
                    <JinroERP
                        userEmail={userEmail}
                        getAccessTokenSilently={getAccessTokenSilently}
                        loadingState={loadingState}
                        setLoadingState={setLoadingState}
                    />
                </TabPanel>
                <TabPanel value={tabValue} index={2} id="duty-tabpanel-2" aria-labelledby="duty-tab-2">
                    <Box sx={{ p: 1 }}>
                        <DutyConfiguration
                            userEmail={userEmail}
                            getAccessTokenSilently={getAccessTokenSilently}
                            loadingState={loadingState}
                            setLoadingState={setLoadingState}
                            itemFormulasRows={itemFormulasRows}
                            setItemFormulasRows={setItemFormulasRows}
                        />
                    </Box>
                </TabPanel>
            </Paper>
            <CircularLoading loading={loadingState.circularLoading} />
            <CustomSnackbar />
        </>
    );
}